import React, { useCallback, useEffect, useMemo } from 'react';
import { UserGroupIcon } from '@revfluence/fresh-icons/solid/esm';
import { useMemberListContext } from '@frontend/app/context/MemberListContext';
import { useMemberSearchQueryVariables } from '@frontend/app/components/MemberTable/hooks';
import { useMemberFieldSchemasQuery, useMemberSearchCountQuery, useMemberSearchQuery } from '@frontend/app/hooks';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import Empty from '@frontend/app/refresh-components/Empty';
import { fetchSocialDataForMembers } from '@frontend/app/containers/GuestHome/Groups/GroupCreatorDetail/useFetchSocialDataForMember';
import { backendServerApiEndpoint } from '@frontend/applications/Shared/serviceHosts';
import { useBackendServerFetch } from '@frontend/app/clients/backendServerClient';
import { useAuth } from '@frontend/context/authContext';
import { ISocialAccount } from '@components';
import { logger } from '@common';
import { useProspectsListContext } from './ProspectsListContext';
import { ProspectsListToolbar } from './ProspectsListToolbar';
import { ProspectCard } from './ProspectCard';
import { TProject } from '../../types';
import { ProjectsPageState } from '../../constants';

interface IProspectsListGridProps {
  renderTableHeader: (refetchTableData?: () => void) => React.ReactNode,
  pageState: ProjectsPageState,
  project: TProject,
  refetchData: () => void,
}
export const ProspectsListGrid: React.FC<IProspectsListGridProps> = ({ renderTableHeader, pageState, project, refetchData }) => {
  const { columnVisibility, searchQuery, searchText, page, selectedMemberIds, selectedMembers, updateSelectedMemberIds, updateSelectedMembers, updateCount, pageSize } = useMemberListContext();

  const { updateSocialProfileMap, setIsFetchingSocialData } = useProspectsListContext();

  const memberSearchQueryVariables = useMemberSearchQueryVariables(searchQuery, columnVisibility, pageSize);

  const skipLoadingMembers = useMemo(() => !searchQuery || !columnVisibility, [searchQuery, columnVisibility]);

  const { loading: loadingMembers, data: { members = null } = {}, refetch: refetchMembers } = useMemberSearchQuery({
    variables: {
      ...memberSearchQueryVariables,
      skip: (page - 1) * pageSize,
      take: pageSize,
    },
    skip: skipLoadingMembers,
    fetchPolicy: 'cache-first',
  });

  const { loading: isTotalLoading, refetch: refetchTotalCount } = useMemberSearchCountQuery({
    variables: {
      query: memberSearchQueryVariables?.query,
    },
    skip: !memberSearchQueryVariables?.query,
  });

  useEffect(() => {
    if (members) {
      updateCount(members.length);
    }
  }, [members, updateCount]);

  const { data: { schemas = null } = {} } = useMemberFieldSchemasQuery();

  const instagramSchema = schemas?.find((schema) => schema.name === 'Instagram');
  const tiktokSchema = schemas?.find((schema) => schema.name === 'TikTok');

  const { backendServerFetchResponse } = useBackendServerFetch();
  const { clientInfo } = useAuth();

  useEffect(() => {
    const usernames = members?.map((member) => {
      const instagramUsername = member.fields[instagramSchema?.id];
      const tiktokUsername = member.fields[tiktokSchema?.id];
      return {
        username: instagramUsername || tiktokUsername,
        network: instagramUsername ? 'instagram' as const : 'tiktok' as const,
      };
    }).filter((item) => item.username);
    if (usernames?.length) {
      setIsFetchingSocialData(true);
      fetchSocialDataForMembers({
        url: `${backendServerApiEndpoint()}/social_account`,
        clientId: clientInfo.id,
        usernames,
        include_social_profile_data: true,
        backendServerFetchResponse,
      })
        .then((response) => {
          if (response.data && Array.isArray(response.data)) {
            const newSocialProfileMap = response.data.reduce((acc, curr) => {
              acc[curr.username] = curr;
              return acc;
            }, {} as Record<string, ISocialAccount>);
            updateSocialProfileMap(newSocialProfileMap);
          }
        })
        .catch((err) => {
          logger.error(err);
        })
        .finally(() => {
          setIsFetchingSocialData(false);
        });
    }
  }, [backendServerFetchResponse, clientInfo.id, instagramSchema?.id, members, setIsFetchingSocialData, tiktokSchema?.id, updateSocialProfileMap]);

  const handleSelect = (id: number, selected: boolean) => {
    if (selected) {
      updateSelectedMemberIds([...selectedMemberIds, id]);
      updateSelectedMembers([...selectedMembers, members?.find((member) => member.id === id)]);
    } else {
      updateSelectedMemberIds(selectedMemberIds.filter((memberId) => memberId !== id));
      updateSelectedMembers(selectedMembers.filter((member) => member.id !== id));
    }
  };

  const refetchTableData = useCallback(() => {
    refetchMembers();
    refetchTotalCount();
  }, [refetchMembers, refetchTotalCount]);

  const isLoading = loadingMembers || isTotalLoading;

  return (
    <div className="h-[calc(100%-79px)] w-full flex flex-col">
      <div className="w-full px-6 pb-3 border-b border-border flex justify-between items-center">
        {renderTableHeader(refetchTableData)}
        <ProspectsListToolbar project={project} />
      </div>
      {isLoading ? (
        <div className="w-full p-6 pt-[10px] flex flex-wrap gap-6 overflow-y-auto">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="w-64 h-[462px] rounded-2xl border border-grey-2" />
          ))}
        </div>
      ) : members?.length === 0 ? (
        <div className="w-full p-6 pt-[10px] flex justify-center items-center min-h-[400px]">
          <Empty
            icon={UserGroupIcon}
            title="No prospects found"
            description={
              searchText
                ? `No results match your search "${searchText}". Try adjusting your search terms.`
                : 'No prospects available to display.'
            }
            primaryCtaProps={undefined}
            size="lg"
          />
        </div>
      ) : (
        <div className="w-full h-full p-6 pt-[10px] flex flex-wrap gap-6 overflow-y-auto">
          {members?.map((prospect) => (
            <ProspectCard
              key={prospect.id}
              member={prospect}
              instagramSchemaId={instagramSchema?.id}
              tiktokSchemaId={tiktokSchema?.id}
              onSelect={handleSelect}
              isSelected={selectedMemberIds.includes(prospect.id)}
              pageState={pageState}
              project={project}
              refetchData={() => {
                refetchMembers();
                refetchTotalCount();
                refetchData();
              }}
              allMembers={members || []}
            />
          ))}
        </div>
      )}
    </div>
  );
};
